import { IconArrowRight } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { AlpharunMark } from "./alpharunMark.js";

export interface ChatSuggestion {
  /** Short display text shown on the suggestion row. */
  label: string;
  /** Full prompt text sent when the suggestion is chosen. */
  prompt: string;
  /**
   * A reply the app writes for the prompt, shown in place of an agent run. Used
   * when the prompt asks the user something first, so the agent picks up from
   * their answer.
   */
  reply?: string;
}

export interface ChatEmptyStateProps {
  heading?: string;
  suggestions?: readonly ChatSuggestion[];
  onSuggestionSelect?: (suggestion: ChatSuggestion) => void;
  /** Shows the suggestions greyed out and not selectable. */
  isSuggestionsDisabled?: boolean;
  /** Rendered below the suggestions (e.g. related chats). */
  children?: ReactNode;
  className?: string;
}

/**
 * Empty state for a chat surface: a "How can I help?" heading with contextual
 * suggestion rows that start a conversation.
 */
export const ChatEmptyState: FC<ChatEmptyStateProps> = ({
  heading = "How can I help?",
  suggestions = [],
  onSuggestionSelect,
  isSuggestionsDisabled = false,
  children,
  className,
}) => (
  <div
    className={twMerge(
      "flex w-full min-w-0 flex-col items-start text-left",
      className,
    )}
  >
    <h2 className="text-xl leading-6 font-semibold text-foreground">
      {heading}
    </h2>

    {suggestions.length > 0 ? (
      <div className="mt-5 flex w-full flex-col items-stretch gap-2">
        {suggestions.map((suggestion) => (
          <AriaButton
            key={suggestion.label}
            className="flex min-w-0 items-center gap-2 rounded-lg bg-muted px-3 py-2 text-left text-xs leading-4 font-medium text-foreground ring-1 ring-border-light transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground enabled:hover:bg-sidebar-accent disabled:cursor-not-allowed disabled:opacity-50"
            isDisabled={isSuggestionsDisabled}
            onPress={() => onSuggestionSelect?.(suggestion)}
          >
            <AlpharunMark />
            <span className="min-w-0 flex-1 break-words whitespace-normal">
              {suggestion.label}
            </span>
            <IconArrowRight className="size-4 shrink-0 text-muted-foreground" />
          </AriaButton>
        ))}
      </div>
    ) : null}

    {children}
  </div>
);
